import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useLayoutEffect,
} from 'react'
import { Okpd2 } from '../../../../../Org/Handbook/types'
import { OrganizationInfo } from '../../../../../Org/Home/types'

import { ContractPerformance, Country } from '../../../../types'
import { CurrencyList } from '../../../types'

import { ContractPerformanceDoc, PerformanceDocPosition } from '../../types'

import LoadingIcon from '../../../../../../../components/LoadingIcon'
import HoverTooltip from '../../../../../../../components/HoverTooltip'

type Props = {
    perfAction: boolean
    performanceGuid: string | undefined
    perfDocNumber: number
    theme: string
    performanceDoc: ContractPerformanceDoc
    selected: boolean
    currencyList: CurrencyList[]
    onClick: (perfDoc_position_guid: string) => void
    onRightClick: (perfDoc_position_guid: string) => void
    onDeleteRow: (perfDoc_position_guid: string) => void
    onClickEdit: (perfDoc_position_guid: string) => void
}

const capitalizeFirstChar = (str: string) => {
    if (str.length === 0) {
        return '-' // Возвращаем пустую строку, если входная строка пуста
    }
    return str.charAt(0).toUpperCase() + str.slice(1)
}

function formatCountryOfOriginStrict(src: Country | null | undefined): string {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return ''

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // убрать дубли, сохраняя порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique.join(', ')
}

function getCountryListOrderedStrict(
    src: Country | null | undefined
): string[] {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return []

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // dedupe (без регистра), сохранив порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique
}

function purchaseTypeLabel(code?: string | null): string {
    switch ((code ?? '').toUpperCase()) {
        case 'G':
            return 'Товар'
        case 'W':
            return 'Работа'
        case 'S':
            return 'Услуга'
        default:
            return '' // неизвестно/пусто
    }
}

function formatTotalCount(totalCount: number) {
    // Получаем число с 5 знаками после запятой (с округлением)
    let str = totalCount.toFixed(5)
    // Если есть десятичная часть, удаляем лишние нули
    if (str.indexOf('.') !== -1) {
        // Удаляем концевые нули
        str = str.replace(/0+$/, '')
        // Если после удаления нулей осталась точка, убираем её
        str = str.replace(/\.$/, '')
    }
    // Заменяем точку на запятую
    return str.replace('.', ',')
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const PerformanceDocTableRow: React.FC<Props> = (props) => {
    const [expanded, setExpanded] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [positions, setPositions] = useState<PerformanceDocPosition[] | null>(
        null
    )

    const contentRef = useRef<HTMLDivElement | null>(null)
    const [maxH, setMaxH] = useState<number>(0)
    const [opacity, setOpacity] = useState(0)

    const loadPositions = async () => {
        setLoading(true)
        setError(null)
        try {
            const performance_contract_guid = props.performanceGuid
            const docCode = props.performanceDoc.performance_contract_doc_code
            const docNum = props.performanceDoc.performance_contract_doc_number
            const docDate = props.performanceDoc.performance_contract_doc_date // уже YYYY-MM-DD

            const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc/positions?performance_contract_guid=${performance_contract_guid}&performance_contract_position_guid=${props.performanceDoc.performance_contract_position_guid}&doc_number=${docNum}&doc_date=${docDate}&doc_code=${docCode}`
            const res = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
            const json: PerformanceDocPosition[] = await res.json()
            setPositions(json ?? [])
        } catch (e: any) {
            console.error(e)
            setError('Не удалось загрузить позиции')
        } finally {
            setLoading(false)
        }
    }

    const onToggle = async (e: React.MouseEvent) => {
        e.stopPropagation()

        // при первом раскрытии подгружаем
        if (!expanded && !positions) {
            await loadPositions()
        }

        // измеряем текущую высоту контента
        const el = contentRef.current
        if (!expanded) {
            // раскрываем
            setExpanded(true)
            requestAnimationFrame(() => {
                const h = el?.scrollHeight ?? 0
                setMaxH(h)
                setOpacity(1)
                // после завершения анимации зафиксируем auto, чтобы блок рос с содержимым
                setTimeout(() => {
                    setMaxH(el?.scrollHeight ?? h)
                }, 300)
            })
        } else {
            // сворачиваем
            const h = el?.scrollHeight ?? 0
            setMaxH(h) // фиксируем текущую
            setOpacity(0) // плавно гасим
            requestAnimationFrame(() => {
                setMaxH(0) // анимируем к 0
            })
            setTimeout(() => setExpanded(false), 300)
        }
    }

    return (
        <div className="flex flex-col">
            {/* строка документа */}
            <div
                className={
                    props.perfAction
                        ? 'flex flex-row gap-1 mx-1 rounded text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]'
                        : props.selected
                          ? 'plan-position-table-row-selected'
                          : 'plan-position-table-row'
                }
                onClick={() => {
                    if (props.perfAction == false) {
                        props.onClick(
                            props.performanceDoc
                                .performance_contract_position_guid
                        )
                    }
                }}
                onContextMenu={(e) => {
                    e.preventDefault() // Отключает стандартное контекстное меню
                    if (props.perfAction == false) {
                        props.onRightClick(
                            props.performanceDoc
                                .performance_contract_position_guid
                        )
                    }
                }}
                // onDoubleClick={() => props.onDoubleClick(props.performance.guid)}
            >
                {props.perfAction ? (
                    <div className="flex w-8 h-[40px] items-center justify-center text-center"></div>
                ) : (
                    <div className="flex w-8 h-[40px] items-center justify-center text-center">
                        <input
                            type="checkbox"
                            checked={props.selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                                props.onClick(
                                    props.performanceDoc
                                        .performance_contract_position_guid
                                )
                            }}
                        />
                    </div>
                )}

                <div className="flex w-8 items-center justify-center text-center ">
                    {props.perfDocNumber}
                </div>
                <div className="flex flex-1 items-center justify-start text-left">
                    {`${
                        props.performanceDoc.performance_contract_doc_name
                            ? capitalizeFirstChar(
                                  props.performanceDoc
                                      .performance_contract_doc_name
                              )
                            : '-'
                    } № 
                            ${
                                props.performanceDoc
                                    .performance_contract_doc_number
                                    ? props.performanceDoc
                                          .performance_contract_doc_number
                                    : '-'
                            }`}
                </div>
                <div className="flex w-[1px] h-full items-center justify-center text-center rounded bg-[#464447]"></div>
                <div className="flex flex-row w-[215px] text-left items-center justify-center">
                    <div
                        className={`flex w-[95px] p-1 items-center justify-center text-center rounded`}
                    >
                        {props.performanceDoc.performance_contract_doc_date
                            ?.split('-')
                            .reverse()
                            .join('-')
                            .replace(/[\-]/g, '.')}
                    </div>
                    <div
                        className={`flex w-[120px] p-1 items-center justify-end text-right rounded`}
                    >
                        {formatNumber(props.performanceDoc.total_rub)}
                    </div>
                </div>
                <div className="flex w-[1px] h-full items-center justify-center text-center rounded bg-[#464447]"></div>
                <div className="flex w-8 text-right items-center justify-center">
                    <button
                        className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#e4e6e8] dark:hover:bg-[#656365]"
                        onClick={onToggle}
                        aria-label="toggle-positions"
                        disabled={loading} // блокируем повторные клики при загрузке
                    >
                        {loading ? (
                            <svg
                                className={`animate-spin h-4 w-4 ${
                                    props.theme === 'dark'
                                        ? 'text-[#FFFFFF]'
                                        : 'text-[#181818]'
                                }`}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                ></path>
                            </svg>
                        ) : (
                            <svg
                                className={`w-5 h-5 transition-transform duration-300 ${
                                    expanded ? 'rotate-90' : '-rotate-90'
                                }`}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="angle-down"
                            >
                                <path
                                    fill={
                                        props.theme === 'dark'
                                            ? '#FFFFFF'
                                            : '#181818'
                                    }
                                    d="m8.5 12.8 5.7 5.6c.4.4 1 .4 1.4 0 .4-.4.4-1 0-1.4l-4.9-5 4.9-5c.4-.4.4-1 0-1.4-.2-.2-.4-.3-.7-.3-.3 0-.5.1-.7.3l-5.7 5.6c-.4.5-.4 1.1 0 1.6 0-.1 0-.1 0 0z"
                                ></path>
                            </svg>
                        )}
                    </button>
                </div>
                {props.perfAction ? (
                    <>
                        <div className="flex w-8 text-right items-center justify-center"></div>
                        <div className="flex w-8 text-right items-center justify-center"></div>
                    </>
                ) : (
                    <>
                        <div className="flex w-8 text-right items-center justify-center">
                            <HoverTooltip tooltipText="Редактировать">
                                <button
                                    onClick={() =>
                                        props.onClickEdit(
                                            props.performanceDoc
                                                .performance_contract_position_guid
                                        )
                                    }
                                    className="w-6 h-6 grid rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="edit-alt"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M5,18H9.24a1,1,0,0,0,.71-.29l6.92-6.93h0L19.71,8a1,1,0,0,0,0-1.42L15.47,2.29a1,1,0,0,0-1.42,0L11.23,5.12h0L4.29,12.05a1,1,0,0,0-.29.71V17A1,1,0,0,0,5,18ZM14.76,4.41l2.83,2.83L16.17,8.66,13.34,5.83ZM6,13.17l5.93-5.93,2.83,2.83L8.83,16H6ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        </div>
                        <div className="flex w-8 text-right items-center justify-center">
                            <HoverTooltip tooltipText="Удалить">
                                <button
                                    onClick={() =>
                                        props.onDeleteRow(
                                            props.performanceDoc
                                                .performance_contract_position_guid
                                        )
                                    }
                                    className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701f1f]"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="trash-alt"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        </div>
                    </>
                )}
            </div>

            {/* аккордеон */}
            <div
                ref={contentRef}
                aria-expanded={expanded}
                className={`${expanded ? 'mt-1 mx-1 mb-2' : ''} flex flex-row rounded text-[11px] bg-[#e4e6e8] dark:bg-[#242225] overflow-hidden
              transition-[max-height] duration-300 ease-in-out`}
                style={{
                    maxHeight: expanded ? maxH : 0,
                    opacity,
                    transition:
                        'max-height 300ms ease-in-out, opacity 250ms ease-in',
                }}
            >
                {/* шапка таблицы */}
                {expanded && (
                    <>
                        <div className="flex flex-col flex-1">
                            <div className="flex flex-row gap-1 h-[40px] p-[5px] items-center rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                                <div className="flex w-[40px] h-[40px] items-center justify-center text-center">
                                    №
                                </div>
                                <div className="flex flex-1 items-center justify-center text-center">
                                    Наименование позиции
                                </div>

                                <div className="flex w-[150px] items-center justify-center">
                                    Единица измерения
                                </div>

                                <div className="flex w-[120px] items-center justify-center">
                                    Количество (объем)
                                </div>

                                <div className="flex w-[120px] items-center justify-center">
                                    Цена за единицу
                                </div>

                                <div className="flex w-[120px] items-center justify-center">
                                    Сумма
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex w-full h-[48px] items-center justify-center">
                                    <div className="flex w-full h-full items-center justify-center">
                                        <LoadingIcon />
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="px-3 py-2 text-red-400 text-sm">
                                    {error}
                                </div>
                            ) : (
                                (positions ?? []).map((p, i) => {
                                    const qty = Number(p.qty ?? 0)

                                    const unit =
                                        p.currency_digital_code != 643
                                            ? p.unit_price
                                            : p.rub_unit_price
                                    const sum = unit ? unit * qty : 0

                                    const rub_unit =
                                        p.currency_digital_code != 643
                                            ? unit
                                                ? unit *
                                                  (p.exchange_rate
                                                      ? p.exchange_rate
                                                      : 0)
                                                : 0
                                            : 0

                                    const rub_sum =
                                        p.currency_digital_code != 643
                                            ? unit
                                                ? unit *
                                                  (p.exchange_rate
                                                      ? p.exchange_rate
                                                      : 0) *
                                                  qty
                                                : 0
                                            : 0

                                    return (
                                        <div
                                            key={p.guid}
                                            className="flex flex-row gap-1 p-1 py-2 mt-1 items-center rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#2A282B]"
                                        >
                                            <div className="flex w-[40px] items-center justify-center text-center">
                                                {p.ordinal}
                                            </div>
                                            <div className="flex flex-1 items-center justify-start text-left">
                                                {p.contract_position_name}
                                            </div>

                                            <div className="flex w-[150px] items-center justify-center text-center">
                                                {p.okei_name ?? ''}
                                            </div>

                                            <div className="flex w-[120px] items-center justify-center text-center">
                                                {qty}
                                            </div>

                                            <div className="flex flex-col w-[120px] px-1 items-end justify-end text-right">
                                                {rub_unit != 0 && (
                                                    <div>
                                                        {formatNumber(rub_unit)}
                                                    </div>
                                                )}
                                                <HoverTooltip
                                                    tooltipText={
                                                        p.currency_digital_code !=
                                                        643
                                                            ? 'Курс: ' +
                                                              p.exchange_rate
                                                            : 'Российский рубль'
                                                    }
                                                >
                                                    <div
                                                        className={`${p.currency_digital_code != 643 ? 'text-[#6A6A6A] dark:text-[#8A8A8A]' : ''}`}
                                                    >
                                                        {props.currencyList.find(
                                                            (cur) =>
                                                                cur.num_code ===
                                                                String(
                                                                    p.currency_digital_code
                                                                )
                                                        )?.char_sign == '₽'
                                                            ? ''
                                                            : props.currencyList.find(
                                                                  (cur) =>
                                                                      cur.num_code ===
                                                                      String(
                                                                          p.currency_digital_code
                                                                      )
                                                              )?.char_sign}{' '}
                                                        {formatNumber(unit)}
                                                    </div>
                                                </HoverTooltip>
                                            </div>

                                            <div className="flex flex-col w-[120px] px-1 items-end justify-end text-right">
                                                {rub_sum != 0 && (
                                                    <div>
                                                        {formatNumber(rub_sum)}
                                                    </div>
                                                )}
                                                <HoverTooltip
                                                    tooltipText={
                                                        p.currency_digital_code !=
                                                        643
                                                            ? 'Курс: ' +
                                                              p.exchange_rate
                                                            : 'Российский рубль'
                                                    }
                                                >
                                                    <div
                                                        className={`${p.currency_digital_code != 643 ? 'text-[#6A6A6A] dark:text-[#8A8A8A]' : ''}`}
                                                    >
                                                        {props.currencyList.find(
                                                            (cur) =>
                                                                cur.num_code ===
                                                                String(
                                                                    p.currency_digital_code
                                                                )
                                                        )?.char_sign == '₽'
                                                            ? ''
                                                            : props.currencyList.find(
                                                                  (cur) =>
                                                                      cur.num_code ===
                                                                      String(
                                                                          p.currency_digital_code
                                                                      )
                                                              )?.char_sign}{' '}
                                                        {formatNumber(sum)}
                                                    </div>
                                                </HoverTooltip>
                                            </div>
                                        </div>
                                    )
                                })
                            )}
                        </div>
                        <div className="flex w-[1px] h-full items-center justify-center text-center"></div>
                        <div className="flex w-[108px] h-full items-center justify-center text-center"></div>
                    </>
                )}
            </div>
        </div>
    )
}

export default PerformanceDocTableRow
